/**
 * @file PopulationGroupsPage.js
 * @description Manages the display and interactions within the Population Groups page.
 */

import React, { useState } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import PopulationGroupList from './PopulationGroupList';
import AddEditNewGroupForm from './AddEditNewGroupForm';
import ViewSystemGroupForm from './ViewSystemGroupForm';
import SystemPopulationGroupList from './SystemPopulationGroupList';
import style from './PopulationGroup.module.scss';
import PopulationGroupIndividualList from './PopulationGroupIndividualList';
import { ROUTES } from '../../constants/routes';
import { useAuthContext } from '../../store/Contexts';
import { TenantTiers } from '../../utils/data/enums';
import { getText } from '../../i18n';

const TABS = {
    CUSTOM_GROUPS: 'Custom',
    SYSTEM_GROUPS: 'System',
};

const PopulationGroupsPage = () => {
    const navigate = useNavigate();
    const matchNew = useMatch(ROUTES.GROUPS_NEW);
    const matchEdit = useMatch(ROUTES.GROUPS_EDIT);

    const addEditNewGroup = Boolean(matchNew || matchEdit);
    const selectedGroupId = matchEdit?.params?.groupId ?? '';

    const [viewSystemGroup, setViewSystemGroup] = useState(false);
    const [selectedSystemGroupId, setSelectedSystemGroupId] = useState('');

    const { clientTierMeetsRequirement } = useAuthContext();

    const canViewCustomPopulationGroups = clientTierMeetsRequirement(
        TenantTiers.Silver,
    );

    const [activeTab, setActiveTab] = useState(
        canViewCustomPopulationGroups ? TABS.CUSTOM_GROUPS : TABS.SYSTEM_GROUPS,
    );
    const [showIndividualPage, setShowIndividualPage] = useState(false);
    const [selectedPopulationGroup, setSelectedPopulationGroup] = useState({});

    const handleAddNewGroup = savedGroup => {
        setViewSystemGroup(false);
        setShowIndividualPage(false);
        if (savedGroup?.id && !matchEdit) {
            navigate(ROUTES.GROUPS_EDIT.replace(':groupId', savedGroup.id));
        }
    };

    const showAddNewGroupForm = id => {
        setViewSystemGroup(false);
        setShowIndividualPage(false);
        if (id) {
            navigate(ROUTES.GROUPS_EDIT.replace(':groupId', id));
        } else {
            navigate(ROUTES.GROUPS_NEW);
        }
    };

    const showIndividuals = populationGroup => {
        setSelectedPopulationGroup(populationGroup);
        setViewSystemGroup(false);
        setShowIndividualPage(true);
    };

    const returnToList = () => {
        setViewSystemGroup(false);
        setShowIndividualPage(false);
        navigate(ROUTES.GROUPS);
    };

    const handleTabChange = tab => {
        setActiveTab(tab);
    };

    const handleSystemGroupSelect = groupId => {
        setSelectedSystemGroupId(groupId);
        setViewSystemGroup(true);
        setShowIndividualPage(false);
    };

    const renderContent = () => {
        if (addEditNewGroup) {
            return (
                <AddEditNewGroupForm
                    onAddEditGroup={handleAddNewGroup}
                    onReturnToList={returnToList}
                    groupId={selectedGroupId}
                />
            );
        } else if (viewSystemGroup) {
            return (
                <ViewSystemGroupForm
                    groupId={selectedSystemGroupId}
                    onReturnToList={returnToList}
                />
            );
        } else if (showIndividualPage && selectedPopulationGroup) {
            return (
                <PopulationGroupIndividualList
                    populationGroup={selectedPopulationGroup}
                    onReturnToList={returnToList}
                />
            );
        } else {
            return (
                <div className="card">
                    <div className="card-body px-0 py-4">
                        <div className="d-flex flex-column gap-2 pb-3 position-relative">
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <h2 className="page-title float-start ms-4 m-0">
                                    {getText('msg_e744dd6b3c48')}
                                </h2>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {getText('msg_938daefdcfa2')}
                                </p>
                            </div>

                            {canViewCustomPopulationGroups && (
                                <ul className={`${style.tabList2} my-4 m-auto`}>
                                    {Object.values(TABS).map(tab => (
                                        <li
                                            key={tab}
                                            className={`${style.tabItem2} ${
                                                activeTab === tab
                                                    ? style.active
                                                    : ''
                                            }`}
                                            onClick={() => handleTabChange(tab)}
                                        >
                                            {tab}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className={`col-auto`}>
                                {activeTab === TABS.CUSTOM_GROUPS && (
                                    <PopulationGroupList
                                        onAddEditGroup={showAddNewGroupForm}
                                        onViewIndividuals={showIndividuals}
                                    />
                                )}

                                {activeTab === TABS.SYSTEM_GROUPS && (
                                    <SystemPopulationGroupList
                                        onSelectGroup={handleSystemGroupSelect}
                                        onViewIndividuals={showIndividuals}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <div
            className={`page-content ${
                addEditNewGroup ? style.pageContentFill : ''
            }`}
        >
            {renderContent()}
        </div>
    );
};

export default PopulationGroupsPage;
